import type { SubmittedAnomaly, SurveyV2, VocabularyEntry } from '@ghp/domain'
import {
  ANOMALY_EFFECT_LABELS,
  SEVERITY_LABELS,
  fixtureDesignationWarning,
  stormwaterAnomalies,
} from '@ghp/domain'
import { Button, Card, CountBadge, EmptyState, IconButton, InfoNote, Tag } from '@ghp/ui'
import { CheckCircle2, Plus, Trash2, TriangleAlert } from 'lucide-react'

import s from './AnomalyPicker.module.css'

/**
 * La carte « Anomalies relevées » de l'onglet Relevé (#411) — la liste de ce que l'agent a relevé, et
 * la porte du constructeur.
 *
 * Ce que cette carte fait : elle **liste ce qui a été relevé** et ouvre le constructeur
 * (`AnomalyBuilder`), qui compose la phrase — origine × provenance × destination, plus les
 * éléments techniques. Une anomalie est **figée à la saisie** (M19) : une règle changée en 2028
 * ne réécrira pas ce contrôle.
 *
 * ⚠️ **Les deux listes de sélection directe ont été retirées le 21/09/2026**, sur un retour de
 * test (« Anomalies relevées : ne pas oublier d'enlever l'ancien sélecteur »). Elles n'existaient
 * pas au prototype, et surtout : ses colonnes « Destination constatée » et « Éléments techniques
 * relevés » vivent **dans** le constructeur, pas sur cet onglet. Les avoir aux deux endroits
 * laissait l'agent devant deux chemins pour le même relevé, dont un seul compose la phrase.
 *
 * ⚠️ Pas de règles pour autant : `anomaly_rule` (M18) et `suggestAnomalies(survey)` restent à
 * venir — aucun des deux chemins ne déduit quoi que ce soit du relevé.
 *
 * Deux listes seulement, `destination` et `technical` : ce sont les deux qui portent une
 * sévérité, donc les deux dont l'avis de conformité se dérive.
 *
 * ⚠️ **Plus de provenance sous l'anomalie depuis #114** : elle se choisit dans la colonne
 * « Provenance » du constructeur, que GHP administre. Une anomalie relevée avant garde son
 * `location`, que cette carte transporte sans le montrer.
 */
export interface AnomalyPickerProps {
  anomalies: SubmittedAnomaly[]
  /** Fiche transmise : les anomalies se lisent, elles ne bougent plus. */
  disabled?: boolean
  onChange?: (anomalies: SubmittedAnomaly[]) => void
  /**
   * Ouvrir le **constructeur d'anomalie** — la grille origine × destination du prototype
   * validé (`AnomalyBuilder`), qui compose la phrase au lieu de reprendre un libellé seul.
   * Sans rappel, la carte ne propose que la sélection directe des listes du dessous.
   */
  onNewAnomaly?: () => void
  /**
   * Le relevé et le référentiel : une anomalie désignée depuis la grille (#312) se compare à ce
   * que la grille compte encore, et le dit quand elle ne suit plus (M21).
   */
  survey?: SurveyV2
  vocabulary?: readonly VocabularyEntry[]
}

export function AnomalyPicker({
  anomalies,
  disabled,
  onChange,
  onNewAnomaly,
  survey = {},
  vocabulary = [],
}: AnomalyPickerProps) {
  // Dérivées du tableau « Eaux pluviales », qui en est la seule donnée : elles se lisent ici, à
  // côté des autres, et se corrigent là-bas.
  const stormwater = stormwaterAnomalies(survey, vocabulary)

  return (
    <div className={s.sections}>
      <Card className={s.card}>
        <h2 className={s.title}>
          Anomalies relevées <CountBadge>{anomalies.length + stormwater.length}</CountBadge>
          {/* Le geste du prototype, à sa place : l'en-tête de la carte « Anomalies relevées ». */}
          {onNewAnomaly !== undefined && (
            <Button className={s.newAnomaly} size="sm" disabled={disabled} onClick={onNewAnomaly}>
              <Plus size={18} aria-hidden />
              Nouvelle anomalie
            </Button>
          )}
        </h2>
        {anomalies.length + stormwater.length === 0 ? (
          <EmptyState
            align="start"
            icon={<CheckCircle2 size={18} aria-hidden />}
            title="Aucune anomalie"
          >
            {/* Le texte du prototype, verbatim : les listes « ci-dessous » ont quitté l'onglet,
                la saisie passe par le constructeur. */}
            Ouvrez la grille de saisie via « Nouvelle anomalie ».
          </EmptyState>
        ) : (
          <ul className={s.retained}>
            {anomalies.map((anomaly, index) => (
              <li key={`${anomaly.sentence}-${String(index)}`}>
                <div className={s.head}>
                  <Tag tone={anomaly.severity === 'bad' ? 'amber' : 'neutral'}>
                    {SEVERITY_LABELS[anomaly.severity]}
                  </Tag>
                  <span className={s.sentence}>{anomaly.sentence}</span>
                  <IconButton
                    label={`Retirer « ${anomaly.sentence} »`}
                    disabled={disabled}
                    onClick={() => onChange?.(anomalies.filter((_, at) => at !== index))}
                  >
                    <Trash2 size={18} aria-hidden />
                  </IconButton>
                </div>

                <DesignationWarning anomaly={anomaly} survey={survey} vocabulary={vocabulary} />
              </li>
            ))}
            {stormwater.map((anomaly) => (
              <li key={anomaly.key}>
                <div className={s.head}>
                  <Tag tone="amber">{SEVERITY_LABELS.bad}</Tag>
                  <span className={s.sentence}>
                    {anomaly.effect === 'derogation'
                      ? `${anomaly.sentence} (${ANOMALY_EFFECT_LABELS.derogation})`
                      : anomaly.sentence}
                  </span>
                </div>
                <InfoNote>Se corrige dans le tableau « Eaux pluviales ».</InfoNote>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

/** L'anomalie désignée que la grille ne couvre plus — la règle du domaine, rien de recalculé ici. */
function DesignationWarning({
  anomaly,
  survey,
  vocabulary,
}: {
  anomaly: SubmittedAnomaly
  survey: SurveyV2
  vocabulary: readonly VocabularyEntry[]
}) {
  const warning = fixtureDesignationWarning(anomaly, survey, vocabulary)

  return warning === null ? null : (
    <InfoNote tone="warn" icon={<TriangleAlert size={14} />}>
      {warning}
    </InfoNote>
  )
}
